// React
import React, { useState, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import Typography from '@material-ui/core/Typography';
import { Button, Link, Snackbar } from "@material-ui/core";
import Alert from '@material-ui/lab/Alert';
import MuiAlert from '@material-ui/lab/Alert';
import VpnKeyOutlinedIcon from '@material-ui/icons/VpnKey';
import InfoOutlined from '@material-ui/icons/InfoOutlined';
import Close from '@material-ui/icons/Close';
// Modus Icons - outlined as React components (via @svgr/webpack)
import { ReactComponent as UserPasskeyIcon } from '@trimble-oss/modus-icons/dist/modus-outlined/svg/user-passkey.svg';
import { ReactComponent as AuthenticatorIcon } from '@trimble-oss/modus-icons/dist/modus-outlined/svg/qr-code.svg';
import { ReactComponent as SecondaryEmailIcon } from '@trimble-oss/modus-icons/dist/modus-outlined/svg/email.svg';

import VisibilityOffOutlined from '@material-ui/icons/VisibilityOffOutlined';
import VisibilityOutlined from '@material-ui/icons/VisibilityOutlined';
import { external_links, flags } from "../../../../feature_flags";
import SecurityMethodRow from "./securityMethodRow";
import SecurityMethodAccordion from "./SecurityMethodAccordion";
import PasskeyItem from "./passkeyItem";
import IframeDialog from "../../../../components/iframeDialog/iframeDialog";
import "./_signInAndSecurity.scss";
import { useSelector } from "react-redux";
import FieldSkeleton from "../../../../components/skeleton/FieldSkeleton";


/**
 * SignInAndSecurity Component
 * New UX for Sign-in & Security section
 */
const SignInAndSecurity = ({ isTIDUser, isSocialUser, mfaConfig, endpoints, getFrameUrl, onChanges }) => {
     const hasIdentity = useSelector(state => state.userManager?.identity);
     const passkeyConfig = useSelector(state => state.userManager?.identity?.passkey_config);
     const totpConfig = useSelector(state => state.userManager?.identity?.totp_config) || {};
     const secondaryEmailConfig = useSelector(state => state.userManager?.identity?.mfa_config?.configured_mfa_settings?.EMAIL_MFA) || {};
     const backup_code_config = useSelector(state => state.userManager?.identity?.mfa_config?.backup_codes);
     
     // Filter passkeys by type
     const devicePasskeys = passkeyConfig ? passkeyConfig.filter(pk => !pk.is_security_key) : [];
     const securityKeyPasskeys = passkeyConfig ? passkeyConfig.filter(pk => pk.is_security_key) : [];
     
     // State for iframe dialog
     const [frameUrl, setFrameUrl] = useState(null);
     
     // State for email visibility
     const [isEmailVisible, setIsEmailVisible] = useState(false);
     
     // State for toast notifications
     const [showToast, setShowToast] = useState(false);
     const [toastMessage, setToastMessage] = useState('');
     
     // State for accordion expanded states (all collapsed by default)
     const [expandedAccordions, setExpandedAccordions] = useState({
          passkeys: false,
          authenticator: false,
          secondaryEmail: false
     });
     
     /**
      * Check for passkey_created query param on mount
      * Shows toast and removes the param from URL
      */
     useEffect(() => {
          const urlParams = new URLSearchParams(window.location.search);
          if (urlParams.get('passkey_created') === 'true') {
               setToastMessage('passkey_added');
               setShowToast(true);
               
               // Remove the query param from URL
               urlParams.delete('passkey_created');
               const newUrl = urlParams.toString() 
                    ? `${window.location.pathname}?${urlParams.toString()}`
                    : window.location.pathname;
               window.history.replaceState({}, '', newUrl);
          }
     }, []);
     
     /**
      * Handles accordion expand/collapse toggle
      * @param {string} panel - The panel identifier
      */
     const handleAccordionChange = (panel) => (event, isExpanded) => {
          setExpandedAccordions(prev => ({
               ...prev,
               [panel]: isExpanded
          }));
     };
     
     /**
      * Formats Unix timestamp to locale date string
      * @param {number} time - Unix timestamp
      * @returns {string} Formatted date string
      */
     const getLocaleTime = (time) => {
          const myDate = new Date(time * 1000);
          return myDate.toLocaleDateString('en-us', { year: "numeric", month: "short", day: "numeric" });
     };
     
     /**
      * Masks an email address according to security rules:
      * - Reveal first and last characters of mailbox prefix
      * - Reveal first and last characters of domain name
      * - Reveal entire TLD
      * - Use exactly 4 asterisks (****) for all masked segments
      * @param {string} email - The email address to mask
      * @returns {string} Masked email address
      */
     const maskEmail = (email) => {
          if (!email || !email.includes('@')) return email;
          
          const [mailbox, domain] = email.split('@');
          const domainParts = domain.split('.');
          
          // Mask mailbox: first char + **** + last char
          let maskedMailbox = mailbox;
          if (mailbox.length > 2) {
               maskedMailbox = mailbox[0] + '****' + mailbox[mailbox.length - 1];
          } else if (mailbox.length === 2) {
               maskedMailbox = mailbox[0] + '****' + mailbox[1];
          }
          
          // Mask domain name (without TLD): first char + **** + last char
          let maskedDomain = domainParts[0];
          if (domainParts.length > 1) {
               const domainName = domainParts[0];
               if (domainName.length > 2) {
                    maskedDomain = domainName[0] + '****' + domainName[domainName.length - 1];
               } else if (domainName.length === 2) {
                    maskedDomain = domainName[0] + '****' + domainName[1];
               }
               // Add TLD (fully visible)
               const tld = domainParts.slice(1).join('.');
               maskedDomain = maskedDomain + '.' + tld;
          }
          
          return maskedMailbox + '@' + maskedDomain;
     };
     
     /**
      * Toggles email visibility
      */
     const toggleEmailVisibility = (e) => {
          e.preventDefault();
          setIsEmailVisible(!isEmailVisible);
     };
     
     /**
      * Opens iframe dialog with the provided URL
      * @param {string} url - The URL to open in the iframe
      * @param {string} action - Optional action parameter
      * @param {any} data - Optional data parameter
      */
     const openFrame = (url, action, data) => {
          const encodedUrl = getFrameUrl(url, action, data);
          setFrameUrl(encodedUrl);
     };

     /**
      * Closes iframe dialog and handles any completion actions
      * @param {Object} data - Data returned from iframe on completion
      */
     const closeFrame = (data) => {
          if (data?.multifactor_changed || data?.passkey_changed || data?.authenticator_changed) {
               // Trigger parent refresh if changes were made
               if (onChanges) {
                    onChanges();
               }
          }
          if(data?.passkey_renamed){
               setToastMessage('passkey_renamed');
               setShowToast(true);
          }
          if(data?.passkey_deleted){
               setToastMessage('passkey_deleted');
               setShowToast(true);
          }
          // Show success toast for authenticator rename
          if (data?.authenticator_changed) {
               setToastMessage('authenticator_renamed');
               setShowToast(true);
          }
          if (data?.authenticator_removed) {
               setToastMessage('authenticator_removed');
               setShowToast(true);
          }
          if (data?.authenticator_enabled) {
               setToastMessage('authenticator_added');
               setShowToast(true);
          }
          if(data?.secondary_email_added){
               setToastMessage('secondary_email_added');
               setShowToast(true);
          }
          if(data?.secondary_email_removed){
               setToastMessage('secondary_email_removed');
               setShowToast(true);
          }
          setFrameUrl(null);
     };
     
     /**
      * Closes the toast notification
      */
     const closeToast = () => {
          setShowToast(false);
     };
     
     // Handlers for security method actions
     const handleCreatePasskey = () => {
          const redirect_url = getFrameUrl(endpoints.createPasskey,"PASSKEY_MFA_CREATE");
          window.location.replace(redirect_url)
     };

     const handleAddAuthenticatorApp = () => {
          openFrame(endpoints.enableTotpMfa);
     };

     const handleAddSecondaryEmail = () => {
          openFrame(endpoints.enableEmailMfa);
     };

     // Passkey management handlers
     const handleRenamePasskey = (e, passkey) => {
          e.preventDefault();
          const passkeyData = window.btoa(Array.from(new TextEncoder().encode(JSON.stringify(passkey)), byte => String.fromCharCode(byte)).join(''));;
          openFrame(endpoints.managePasskey, "edit_passkey", passkeyData);
     };

     const handleDeletePasskey = (e, passkey) => {
          e.preventDefault();
          const passkeyData = window.btoa(Array.from(new TextEncoder().encode(JSON.stringify(passkey)), byte => String.fromCharCode(byte)).join(''));;
          openFrame(endpoints.managePasskey, "delete_passkey", passkeyData);
     };

     // Authenticator app handlers
     const handleRenameAuthenticator = (e) => {
          e.preventDefault();
          const totpData = window.btoa(Array.from(new TextEncoder().encode(JSON.stringify(totpConfig)), byte => String.fromCharCode(byte)).join(''));
          openFrame(endpoints.renameTotpMfa, "rename", totpData);
     };

     const handleDeleteAuthenticator = (e) => {
          e.preventDefault();
          openFrame(endpoints.disableTotpMfa, "disable");
     };

     // Secondary email handlers
     const handleDeleteSecondaryEmail = (e) => {
          e.preventDefault();
          openFrame(endpoints.disableEmailMfa, "disable");
     };

     // Recovery code handlers
     const handleViewRecoveryCodes = (e) => {
          e.preventDefault();
          openFrame(endpoints.recoveryCodeMfa);
     };

     return (
          <div className="sign-in-and-security">
               <h4 className="section-title">
                    <FormattedMessage defaultMessage="Sign-in and security" />
               </h4>
               
               <div className="section-intro">
                    <Typography variant="body1" component="p" className="intro-text">
                         <FormattedMessage 
                              defaultMessage="Secure your account with an extra layer of protection. Add one or more methods below to verify your identity when you sign in. {url}" 
                              values={
                                   {
                                        url: <Link className="custom-button__focus" target="_blank"
                                        rel="noopener noreferrer" href={external_links.sign_in_and_security_learn_more}> <FormattedMessage defaultMessage="Learn more about securing your account" /></Link>
                                   } 
                              } 
                         />
                    </Typography>
               </div>

               {hasIdentity ? (
               <div className="security-methods">
                    {/* Passkeys Section */}
                    {passkeyConfig && passkeyConfig?.length > 0 ? (
                         <SecurityMethodAccordion
                              id="passkeys"
                              expanded={expandedAccordions.passkeys}
                              onChange={handleAccordionChange('passkeys')}
                              icon={<UserPasskeyIcon width={18} height={18} className="passkey_icon" aria-hidden="true" />}
                              title={<FormattedMessage defaultMessage="Passkeys" />}
                              subtitle={
                                   <>
                                        {passkeyConfig?.length === 1 && <FormattedMessage defaultMessage="1 passkey"/>}
                                        {passkeyConfig?.length === 2 && <FormattedMessage defaultMessage="2 passkeys"/>}
                                        {passkeyConfig?.length === 3 && <FormattedMessage defaultMessage="Max of 3 passkeys"/>}
                                   </>
                              }
                         >
                              {/* Device Passkeys Section */}
                              {devicePasskeys.length > 0 && (
                                   <>
                                        <div className="passkey-subsection-title">
                                             <FormattedMessage defaultMessage="Passkeys on your device" />
                                        </div>
                                        <div className="security-method-list">
                                             {devicePasskeys.map((passkey) => (
                                                  <PasskeyItem
                                                       key={passkey?.id}
                                                       passkey={passkey}
                                                       onRename={handleRenamePasskey}
                                                       onDelete={handleDeletePasskey}
                                                       getLocaleTime={getLocaleTime}
                                                  />
                                             ))}
                                        </div>
                                   </>
                              )}

                              {/* Security Key Passkeys Section */}
                              {securityKeyPasskeys.length > 0 && (
                                   <>
                                        <div className="passkey-subsection-title">
                                             <FormattedMessage defaultMessage="Passkeys on a security key" />
                                        </div>
                                        <div className="security-method-list">
                                             {securityKeyPasskeys.map((passkey) => (
                                                  <PasskeyItem
                                                       key={passkey.id}
                                                       passkey={passkey}
                                                       onRename={handleRenamePasskey}
                                                       onDelete={handleDeletePasskey}
                                                       getLocaleTime={getLocaleTime}
                                                  />
                                             ))}
                                        </div>
                                   </>
                              )}

                              {/* Add backup passkey button - show only when less than 3 passkeys */}
                              {passkeyConfig?.length < 3 && (
                                   <div className="add-backup-passkey-container">
                                        <Button
                                             variant="outlined"
                                             color="primary"
                                             onClick={handleCreatePasskey}
                                             className="add-backup-passkey-button"
                                             disableRipple
                                        >
                                             <FormattedMessage defaultMessage="Add a backup passkey" />
                                        </Button>
                                   </div>
                              )}
                         </SecurityMethodAccordion>
                    ) : (
                              <SecurityMethodRow
                                   icon={<UserPasskeyIcon width={18} height={18} className="passkey_icon" aria-hidden="true" />}
                                   title={<FormattedMessage defaultMessage="Passkeys" />}
                                   description={
                                        <FormattedMessage defaultMessage="Sign in faster and more securely with passkeys. Passkeys replace passwords with your fingerprint, face ID, or device screen lock, making sign in simple and safe." />
                                   }
                                   badge={<FormattedMessage defaultMessage="Recommended" />}
                                   actionLabel={<FormattedMessage defaultMessage="Create" />}
                                   onActionClick={handleCreatePasskey}
                              />
                         )}
                     {/* Show authenticator app and secondary email section if user is TID user or social user with mfa configured */}    
                    {(isTIDUser || (isSocialUser && mfaConfig && mfaConfig.configured_mfa_settings 
                              && Object.keys(mfaConfig.configured_mfa_settings).length > 0)) && (
                    <>
                    {/* Authenticator App Section */}
                    {Object.keys(totpConfig)?.length > 0 ? (
                         <SecurityMethodAccordion
                              id="authenticator"
                              expanded={expandedAccordions.authenticator}
                              onChange={handleAccordionChange('authenticator')}
                              icon={<AuthenticatorIcon width={26} height={26} className="security-method-section-icon" aria-hidden="true" />}
                              title={<FormattedMessage defaultMessage="Authenticator app" />}
                         >
                              <div className="security-method-list">
                                   <div className="security-method-item">
                                        <div className="security-method-item-content">
                                             <div className="security-method-item-header">
                                                  <div className="security-method-item-title">{totpConfig?.name || "Authenticator"}</div>
                                             </div>
                                             {totpConfig?.created_time && (
                                                  <div className="security-method-item-meta">
                                                  <FormattedMessage 
                                                       defaultMessage="Added {date}"
                                                       values={{
                                                            date: getLocaleTime(totpConfig.created_time)
                                                       }}
                                                  />
                                                  </div>
                                             )}
                                        </div>
                                        <div className="security-method-item-actions">
                                             <button 
                                                  className="security-method-action-link"
                                                  onClick={handleRenameAuthenticator}
                                             >
                                                  <FormattedMessage defaultMessage="Rename" />
                                             </button>
                                             <button 
                                                  className="security-method-delete-btn"
                                                  onClick={handleDeleteAuthenticator}
                                             >
                                                  <Close className="security-method-delete-btn-icon" />
                                             </button>
                                        </div>
                                   </div>
                              </div>
                         </SecurityMethodAccordion>
                    ) : (
                         <SecurityMethodRow
                              icon={<AuthenticatorIcon width={26} height={26} className="modus-icon" aria-hidden="true" />}
                              title={<FormattedMessage defaultMessage="Authenticator app" />}
                              description={
                                   <FormattedMessage defaultMessage="Get a time-sensitive verification code from an app like Google authenticator or Microsoft authenticator. You can only connect one authenticator app." />
                              }
                              actionLabel={<FormattedMessage defaultMessage="Add" />}
                              onActionClick={handleAddAuthenticatorApp}
                         />
                    )}

                    {/* Secondary Email Section */}
                    {Object.keys(secondaryEmailConfig)?.length > 0 ? (
                         <SecurityMethodAccordion
                              id="secondary-email"
                              expanded={expandedAccordions.secondaryEmail}
                              onChange={handleAccordionChange('secondaryEmail')}
                              icon={<SecondaryEmailIcon width={18} height={18} className="modus-icon security-method-section-icon" aria-hidden="true" />}
                              title={<FormattedMessage defaultMessage="Secondary email" />}
                         >
                              <div className="security-method-list">
                                   <div className="security-method-item">
                                        <div className="security-method-item-content">
                                             <div className="security-method-item-header">
                                                  <div className="security-method-item-title">
                                                       {isEmailVisible ? secondaryEmailConfig?.destination : maskEmail(secondaryEmailConfig?.destination)}
                                                  </div>
                                             </div>
                                             {secondaryEmailConfig?.created_time && (
                                             <div className="security-method-item-meta">
                                                  <FormattedMessage 
                                                       defaultMessage="Added {date}"
                                                       values={{
                                                            date: getLocaleTime(secondaryEmailConfig?.created_time)
                                                       }}
                                                  />
                                             </div>
                                             )}
                                        </div>
                                        <div className="security-method-item-actions">
                                             <button 
                                                  className="email-visibility-toggle"
                                                  onClick={toggleEmailVisibility}
                                                  aria-label={isEmailVisible ? "Hide email" : "Show email"}
                                             >
                                                  {isEmailVisible ? (
                                                       <VisibilityOutlined fontSize="small" />
                                                  ) : (
                                                       <VisibilityOffOutlined fontSize="small" />
                                                  )}
                                             </button>
                                             <button 
                                                  className="security-method-delete-btn"
                                                  onClick={handleDeleteSecondaryEmail}
                                             >
                                                  <Close className="security-method-delete-btn-icon" />
                                             </button>
                                        </div>
                                   </div>
                              </div>
                         </SecurityMethodAccordion>
                    ) : (
                         <SecurityMethodRow
                              icon={<SecondaryEmailIcon width={18} height={18} className="modus-icon security-method-section-icon" aria-hidden="true" />}
                              title={<FormattedMessage defaultMessage="Secondary email" />}
                              description={
                                   <FormattedMessage defaultMessage="Get a verification code sent to a different email address. This cannot be the same as your primary email. You can only add one secondary email." />
                              }
                              actionLabel={<FormattedMessage defaultMessage="Add" />}
                              onActionClick={handleAddSecondaryEmail}
                         />
                    )}
                    </>
                    )}
                    {/* Recovery Codes Section */}
                    {flags.show_recovery_codes && backup_code_config ? (
                         <div className="security-method-row">
                              <div className="security-method-header">
                                   <h5 className="security-method-section-title">
                                        <VpnKeyOutlinedIcon className="security-method-section-icon" />
                                        <FormattedMessage defaultMessage="Recovery codes" />
                                   </h5>
                                   <Button
                                        variant="outlined" 
                                        color="primary"
                                        onClick={handleViewRecoveryCodes}
                                        className="security-method-action-button"
                                        disableRipple
                                   >
                                        <FormattedMessage defaultMessage="View" />
                                   </Button>
                              </div>
                              <div className="security-method-list">
                                   <div className="recovery-code-content">
                                        {backup_code_config?.unused_code_length > 0 ? (
                                             <Typography variant="body2" className="recovery-code-text">
                                                  <FormattedMessage 
                                                       defaultMessage="You have {unused_length} unused recovery codes. Store them in a safe place. If you lose access to your other sign-in methods, you can use a recovery code to access your account."
                                                       values={{ unused_length: '' + backup_code_config?.unused_code_length }}
                                                  />
                                             </Typography>
                                        ) : (
                                             <Typography variant="body2" className="recovery-code-text">
                                                  <FormattedMessage defaultMessage="All recovery codes have been used. Generate a new set to ensure continued access to your account." />
                                             </Typography>
                                        )}
                                        
                                        {backup_code_config?.download_alert && (
                                             <Alert icon={<InfoOutlined />} variant="outlined" severity="warning" className="recovery-code-alert">
                                                  <FormattedMessage defaultMessage="You haven't saved your recovery codes in over a year. To ensure you can always access your account, view and download your current codes." />
                                             </Alert>
                                        )}
                                   </div>
                              </div>
                         </div>
                    ) : null}
               </div>
               ) : (
                    <FieldSkeleton />
               )}
               {/* Iframe Dialog for security method configuration */}
               <IframeDialog
                    open={frameUrl !== null}
                    frameUrl={frameUrl}
                    onClose={closeFrame}
                    iframeId="sign-in-security-frame"
                    title="Security Settings"
               />

               {/* Toast notification for success messages */}
               <Snackbar
                    anchorOrigin={{ horizontal: "center", vertical: "top" }}
                    open={showToast}
                    onClose={closeToast}
                    autoHideDuration={6000}
                    className="snackbar"
               >
                    <MuiAlert elevation={6} variant="filled" severity="success" onClose={closeToast}>
                         {toastMessage === 'passkey_added' && (
                              <FormattedMessage defaultMessage="Passkey added" />
                         )}
                         {toastMessage === 'passkey_renamed' && (
                              <FormattedMessage defaultMessage="Your passkey was renamed" />
                         )}
                         {toastMessage === 'passkey_deleted' && (
                              <FormattedMessage defaultMessage="Passkey removed" />
                         )}
                         {toastMessage === 'authenticator_renamed' && (
                              <FormattedMessage defaultMessage="Your authenticator app was renamed" />
                         )}
                         {toastMessage === 'authenticator_removed' && (
                              <FormattedMessage defaultMessage="Authenticator app removed" />
                         )}
                         {toastMessage === 'authenticator_added' && (
                              <FormattedMessage defaultMessage="Authenticator app added" />
                         )}
                         {toastMessage === 'secondary_email_added' && (
                              <FormattedMessage defaultMessage="Secondary email added" />
                         )}
                         {toastMessage === 'secondary_email_removed' && (
                              <FormattedMessage defaultMessage="Secondary email removed" />
                         )}
                    </MuiAlert>
               </Snackbar>
          </div>
     );
};

export default SignInAndSecurity;
